import gsap from 'gsap'
import Texture2D from 'nanogl/texture-2d'
import Animation from 'nanogl-gltf/lib/elements/Animation'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import MeshRenderer from 'nanogl-gltf/lib/renderer/MeshRenderer'
import GltfTypes from 'nanogl-gltf/lib/types/GltfTypes'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { MusicAudio } from '@/config/AudioConfig'
import { HexToVec3 } from '@/webgl/core/Color'
import Deferred from '@/webgl/core/Deferred'
import { RenderContext } from '@/webgl/core/RenderContext'
import Signal from '@/webgl/core/Signal'
import { clamp } from '@/webgl/math'
import AssetDatabase from '@/webgl/resources/AssetDatabase'
import GltfResource from '@/webgl/resources/GltfResource'
import LanesMergeAnimation from '@/webgl/runner/animations/LanesMergeAnimation'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import PortalSpiralChunk from '@/webgl/runner/rendering/chunks/PortalSpiralChunk'
import { backgroundLayer } from '@/webgl/runner/rendering/RenderingUtils'
import ScreenSpiralRenderer from '@/webgl/runner/rendering/ScreenSpiralRenderer'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import MusicSystem from '@/webgl/runner/systems/MusicSystem'
import { IGameSegment } from '@/webgl/runner/systems/SequencingSystem'

export default class NarniaDoorSegment implements IGameSegment {
  world: GameWorld
  renderer: IRunnerRenderer
  onComplete: Signal<void> = new Signal<void>()

  doorGltf: Gltf
  doorAnimation: Animation
  doorRender: boolean = false
  doorAnimationTime: number = 0
  doorAnimationDuration: number = 0

  laneMerge: LanesMergeAnimation

  spiralTex: Texture2D
  screenSpiral: ScreenSpiralRenderer
  doorSpiralChunk: PortalSpiralChunk

  eyeGltf: Gltf
  spiralEye: MeshRenderer
  stopped: boolean = false
  entered: boolean = false

  constructor(readonly id: string = 'narnia_door') {}

  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup): void {
    this.world = world
    this.renderer = renderer

    this.entered = false
    this.laneMerge = new LanesMergeAnimation(renderer.lanes)

    this.doorSpiralChunk = new PortalSpiralChunk(world.time)
    this.screenSpiral = new ScreenSpiralRenderer(renderer.gl)
    /// #if _DEBUG
    // gui.add(this.spiralFlash, "reveal", { min: 0, max: 1 });
    // gui.add(this.spiralFlash, "offsetX", { min: -0.5, max: 0.5 });
    // gui.add(this.spiralFlash, "offsetY", { min: -0.5, max: 0.5 });
    /// #endif
  }

  async load(): Promise<void> {
    await this.loadSpiral()
    await this.loadEye()
    await this.loadDoor()
  }

  async loadSpiral() {
    this.spiralTex = await AssetDatabase.getTexture('pedro_2/narnia_spiral.jpg', this.renderer.gl, {
      wrap: 'clamp',
    }).load()
    this.doorSpiralChunk.setTexture(this.spiralTex)
    this.screenSpiral.setTexture(this.spiralTex)
  }

  async loadEye() {
    const overrides = new MaterialOverrideExtension()
    const chunk = new PortalSpiralChunk(this.world.time)
    chunk.setTexture(this.spiralTex)

    HexToVec3(0xfe8769, chunk.color1)
    HexToVec3(0xfed1c8, chunk.color2)

    overrides.overridePass('glowing_eye', (ctx, m) => {
      const pass = m.getPass('color').pass as UnlitPass
      backgroundLayer(this.renderer.gl, pass)
      pass.inputs.add(chunk)
      return pass
    })

    this.eyeGltf = await new GltfResource('common/glowing_eye.glb', this.renderer.gl, {
      extensions: [overrides],
    }).load()

    this.spiralEye = this.eyeGltf.getElementByName(GltfTypes.NODE, 'glowing_eye').renderable
  }

  async loadDoor() {
    const overrides = new MaterialOverrideExtension()
    overrides.overridePass('portal', (ctx, m) => {
      const pass = m.getPass('color').pass as UnlitPass
      pass.inputs.add(this.doorSpiralChunk)
      return pass
    })

    this.doorGltf = await new GltfResource('pedro_2/narnia_door_2.glb', this.renderer.gl, {
      extensions: [overrides],
    }).load()

    this.doorAnimation = this.doorGltf.animations[0]
    this.doorAnimationTime = 0
    this.doorAnimationDuration = this.doorAnimation.duration
  }

  async start(): Promise<void> {
    this.world.getTrackingSystem().trackImpression({
      unitName: `pedro_simulation_1`,
      unitVariation: `scenario_auto_obstacle_narnia`,
      unitLocation: 'tyce',
    })

    this.stopped = false
    this.doorAnimationTime = 0

    const settings = this.world.settings
    this.doorRender = true

    this.doorGltf.root.setScale(0.5)
    this.doorGltf.root.z = -settings.LANE_LENGTH

    this.world.root.add(this.doorGltf.root)

    const player = this.world.player
    player.direction = 0
    player.lane = 0
    player.locked = true
    gsap.to(player.node, { x: 0, duration: 1.0 })

    this.laneMerge.init()

    await this.laneMerge.play()
    // if (this.stopped) return;

    // await Delay((this.doorAnimationDuration - this.doorAnimationTime) * 1000);
    // if (this.stopped) return;
  }

  async firstFlash(): Promise<void> {
    const defer = new Deferred<void>()
    gsap.to(this.screenSpiral, {
      reveal: 0.5,
      duration: 0.7,
      ease: 'linear',
      onComplete: () => defer.resolve(),
    })
    return defer.promise
  }

  async releaseFlash(): Promise<void> {
    const defer = new Deferred<void>()
    gsap.to(this.screenSpiral, {
      reveal: 1.0,
      duration: 1.0,
      ease: 'linear',
      onComplete: () => defer.resolve(),
    })
    return defer.promise
  }

  async enterNarniaWorld() {
    this.entered = true
    await this.firstFlash()
    if (this.stopped) return

    this.world.systems.get<MusicSystem>(MusicSystem).play(MusicAudio.Narnia)

    this.renderer.glRenderer.setClearColor(0x4ea4bd)
    this.renderer.lanes.setColor(0x4ea4bd)
    this.renderer.smoke.setColor(0xfe8769)
    this.renderer.eye.setScale(1.8)
    this.renderer.background.updateEye(this.spiralEye)
    this.renderer.particles.setActiveVariant('narnia')

    this.world.state.alterJauge(1)

    this.doorRender = false
    this.laneMerge.cleanup()
    this.world.background.activeItems = []
    this.world.background.spawnableItems = ['brocoli', 'pizza']

    await this.releaseFlash()
    if (this.stopped) return

    this.onComplete.emit()
  }

  update(time: TimeEntity): void {
    const settings = this.world.settings
    const paused = settings.scrollSpeed == 0
    const factor = paused ? 0 : 1

    const z = this.doorGltf.root.z + time.unscaledDeltaTime * 2.3 * factor
    this.doorGltf.root.z = Math.min(z, -settings.GRAB_THRESHOLD - 0.2)

    this.doorAnimation.evaluate(this.doorAnimationTime)
    this.doorAnimationTime += time.unscaledDeltaTime * factor
    this.doorAnimationTime = clamp(this.doorAnimationTime, 0, this.doorAnimationDuration)

    if (this.doorAnimationTime == this.doorAnimationDuration && !this.entered) {
      this.enterNarniaWorld()
    }
  }

  render(context: RenderContext) {
    if (this.doorRender) {
      for (const renderable of this.doorGltf.renderables) {
        renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
      }
    }

    this.screenSpiral.render(context)
  }

  stop(): void {
    this.stopped = true
    this.world.player.locked = false
    this.world.root.remove(this.doorGltf.root)
  }
}
